import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { notifications } from '@mantine/notifications';
import styles from './AppBar.module.scss';
import { FiCountry, FiLanguage, useLocale } from '../../contexts/LocaleContext';
import { getFiGrowthBook } from '../../lib/growthbook';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import * as types from '../../types';
import { validateCart } from '../../lib/cart';
import { getProductsBySku } from '../../lib/product';
import { storeShopActions } from '../../reducers/storeShop';
import Twemoji from 'react-twemoji';

function useOnClickOutside(ref: React.RefObject<HTMLElement>, handler: () => void) {
  useEffect(() => {
    const listener = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!ref.current || !target || ref.current.contains(target)) return;
      handler();
    };
    document.addEventListener('pointerdown', listener);
    return () => {
      document.removeEventListener('pointerdown', listener);
    };
  }, [ref, handler]);
}

function useCloseOnScroll(open: boolean, handler: () => void) {
  useEffect(() => {
    if (!open) return;
    const onScroll = () => handler();
    window.addEventListener('scroll', onScroll, { capture: true, passive: true } as EventListenerOptions);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open, handler]);
}

const COUNTRY_NAME_KEYS: Record<FiCountry, string> = {
  [FiCountry.US]: 'united_states',
  [FiCountry.CA]: 'canada',
  [FiCountry.GB]: 'united_kingdom',
};

export default function CountrySelector() {
  const { country, setCountry, language, setLanguage, flagEmoji } = useLocale();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();

  const cart = useSelector((state: types.AppState) => state.storeShop.cart);
  const products = useSelector((state: types.AppState) => state.config.products);
  const internalTester = useSelector((state: types.AppState) => !!state.config.siteConfig.internalTester);

  const fiSupportedCountries = useMemo(
    () =>
      getFiGrowthBook().getFeatureValue<FiCountry[]>('ecom-supported-countries', [FiCountry.US, FiCountry.CA], {
        internalTester,
      }),
    [internalTester],
  );

  const showSelector = fiSupportedCountries.length > 1;

  useOnClickOutside(menuRef, () => setOpen(false));
  useCloseOnScroll(open, () => setOpen(false));

  const { t } = useTranslation();

  const gbEnabled = fiSupportedCountries.includes(FiCountry.GB);

  const options = gbEnabled
    ? ([
        { key: 'US', label: t('united_states_of_america'), emoji: '🇺🇸' },
        { key: 'en-CA', label: t('canada_english'), emoji: '🇨🇦' },
        { key: 'fr-CA', label: t('canada_french'), emoji: '🇨🇦' },
        { key: 'GB', label: t('united_kingdom'), emoji: '🇬🇧' },
      ] as const)
    : ([
        { key: 'US', label: t('united_states_of_america'), emoji: '🇺🇸' },
        { key: 'en-CA', label: t('canada_english'), emoji: '🇨🇦' },
        { key: 'fr-CA', label: t('canada_french'), emoji: '🇨🇦' },
      ] as const);
  const handleSelect = useCallback(
    async (key: string) => {
      let targetCountry: FiCountry;
      let targetLanguage: FiLanguage;

      if (key === 'US') {
        targetCountry = FiCountry.US;
        targetLanguage = FiLanguage.EN_US;
      } else if (key === 'en-CA') {
        targetCountry = FiCountry.CA;
        targetLanguage = FiLanguage.EN_CA;
      } else if (key === 'fr-CA') {
        targetCountry = FiCountry.CA;
        targetLanguage = FiLanguage.FR_CA;
      } else if (key === 'GB') {
        targetCountry = FiCountry.GB;
        targetLanguage = FiLanguage.EN_GB;
      } else {
        return;
      }

      setOpen(false);
      setCountry(targetCountry);
      setLanguage(targetLanguage);

      const shouldValidateCart = getFiGrowthBook().getFeatureValue<boolean>('ecom-validate-product-country', false);

      if (shouldValidateCart && Object.keys(cart.cartItems).length > 0) {
        // t has not been updated with the new language yet, so we need to specify it for the notification
        const lng = targetLanguage === FiLanguage.EN_US ? 'en' : targetLanguage.toLowerCase();
        try {
          const invalidIds = await validateCart(cart, targetCountry);
          if (invalidIds.length > 0) {
            dispatch(storeShopActions.removeCartItems({ ids: invalidIds }));

            const productMap = getProductsBySku(products);
            const invalidProductNames = invalidIds
              .map((id) => {
                const cartItem = cart.cartItems[id];
                if (!cartItem) return null;
                return productMap.get(cartItem.lineItem.sku)?.name ?? cartItem.lineItem.sku;
              })
              .filter(Boolean)
              .join(', ');

            notifications.show({
              message: t('cart_items_not_available_in_country', {
                lng,
                productNames: invalidProductNames || t('cart', { lng }),
                country: t(COUNTRY_NAME_KEYS[targetCountry], { lng }),
                count: invalidIds.length,
              }),
              color: 'red',
            });
          }
        } catch {
          dispatch(storeShopActions.removeAllCartItems());
        }
      }
    },
    [setCountry, setLanguage, cart, products, dispatch, t],
  );

  if (!showSelector) return null;

  return (
    <div className={styles.countrySelector} ref={menuRef}>
      <button
        type="button"
        className={styles.countryButton}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <Twemoji options={{ className: styles.flagIcon }}>{flagEmoji}</Twemoji>
      </button>
      {open && (
        <div className={classNames(styles.countryMenu)} role="menu">
          {options.map((opt) => (
            <button
              key={opt.key}
              type="button"
              className={classNames(styles.countryMenuItem, {
                [styles.selected]:
                  opt.key === 'US'
                    ? country === FiCountry.US
                    : opt.key === 'en-CA'
                    ? country === FiCountry.CA && language === FiLanguage.EN_CA
                    : opt.key === 'fr-CA'
                    ? country === FiCountry.CA && language === FiLanguage.FR_CA
                    : country === FiCountry.GB && language === FiLanguage.EN_GB,
              })}
              onClick={() => void handleSelect(opt.key as string)}
              role="menuitem"
            >
              <Twemoji options={{ className: styles.flagIcon }}>{opt.emoji}</Twemoji>
              <span className={styles.countryMenuLabel}>{opt.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
